import { ErrorHandler, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { MissingTranslationHandler, TranslateLoader, TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, RouterModule, UrlSerializer } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MyMissingTranslationHandler } from './shared/translate/missing-translation-handler';
import { AngularSvgIconModule } from 'angular-svg-icon';
import 'chartjs-adapter-moment';
import { ToastrModule } from 'ngx-toastr';
import { QrPaymentComponent } from './shared/qr-payment/qr-payment.component';
import { ToastNotificationComponent } from './shared/toast-notification/toast-notification.component';
import { ModalComponent } from './services/modal/modal/modal.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { WebpackTranslateLoader } from './shared/translate/translate-loader';
import {  ConfigService, provideConfig } from './services/json-config.service';
import { CustomUrlSerializer } from './shared/custom-url-serializer';
import { AuthInterceptor } from './shared/token-refresh.interceptor';
import { ButtonModule } from './shared/button/button.module';
import { TooltipButtonModule } from './shared/tooltip-button/tooltip-button.module';
import { PreloadSpinnerModule } from './shared/preload-spinner/preload-spinner.module';
import { PricePipeModule } from './pipes/price.module';
import { OauthRoutingModule } from './modules/oauth/oauth-routing.module';
import { LocaleCountryNamePipe } from './pipes/locale-country-name.pipe';
import * as Sentry from '@sentry/angular';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { StepperModule } from './shared/stepper/stepper.module';
import { registerLocaleData } from '@angular/common';
import localeCs from '@angular/common/locales/cs';
import { FeesConfigDropdownComponent } from './shared/fees-config-dropdown/fees-config-dropdown.component';
import { FeesEntryRegularConfigDropdownComponent } from './shared/fees-entry-regular-config-dropdown/fees-entry-regular-config-dropdown.component';
import { HeaderConsultComponent } from './shared/header-consult/header-consult.component';
import { FooterConsultComponent } from './shared/footer-consult/footer-consult.component';
import { ConsultCreateEfektikaStrategyModalComponent } from './services/modal/modal-contents/consult-create-efektika-strategy-modal/consult-create-efektika-strategy-modal.component';
import { OpfFeeConfirmModalComponent } from './services/modal/modal-contents/opf-fee-confirm-modal/opf-fee-confirm-modal.component';
import { NewRegularWithdrawalModalComponent } from './services/modal/modal-contents/new-regular-withdrawal-modal/new-regular-withdrawal-modal.component';
import { TransactionsExportModalComponent } from './services/modal/modal-contents/transactions-export-modal/transactions-export-modal.component';
import { SaveStrategyTemplateModalComponent } from './services/modal/modal-contents/save-strategy-template-modal/save-strategy-template-modal.component';
import { OwlDateTimeModule, OwlNativeDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { TemplateDraftDeleteModalComponent } from './services/modal/modal-contents/template-draft-delete-modal/template-draft-delete-modal.component';
import packageInfo from 'package.json';

registerLocaleData(localeCs);

@NgModule({
    declarations: [
        AppComponent,
        HeaderConsultComponent,
        FooterConsultComponent,
        ModalComponent,
        ToastNotificationComponent,
        QrPaymentComponent,
        NotFoundComponent,
        LocaleCountryNamePipe,
        FeesConfigDropdownComponent,
        FeesEntryRegularConfigDropdownComponent,
        ConsultCreateEfektikaStrategyModalComponent,
        OpfFeeConfirmModalComponent,
        NewRegularWithdrawalModalComponent,
        TransactionsExportModalComponent,
        TemplateDraftDeleteModalComponent,
        SaveStrategyTemplateModalComponent,
    ],
    bootstrap: [AppComponent],
    imports: [
        RouterModule,
        BrowserModule,
        AppRoutingModule,
        ReactiveFormsModule,
        FormsModule,
        ButtonModule,
        PreloadSpinnerModule,
        TooltipButtonModule,
        PricePipeModule,
        OauthRoutingModule,
        StepperModule,
        OwlDateTimeModule,
        OwlNativeDateTimeModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (http: HttpClient) => new WebpackTranslateLoader(http, packageInfo.version),
                deps: [HttpClient],
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: MyMissingTranslationHandler,
            }
        }),
        AngularSvgIconModule.forRoot(),
        BrowserAnimationsModule,
        ToastrModule.forRoot({
            toastComponent: ToastNotificationComponent,
            preventDuplicates: true,
        })],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        TranslatePipe,
        provideConfig(),
        ConfigService,
        // ConfigModule.init(),
        {
            provide: UrlSerializer,
            useClass: CustomUrlSerializer
        },
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: provideAppInitializer,
            useFactory: () => () => null,
            deps: [Sentry.TraceService],
            multi: true,
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideCharts(withDefaultRegisterables()),
        { provide: LOCALE_ID, useValue: 'cs-CZ'},
    ]
})
export class AppModule { }
